// Built-in locked rows (s73 — replaces the virtual `__virt__` locked-row machinery).
//
// Certain template sections (currently only Government IDs) carry built-in,
// undeletable rows — declared in the template spec with `_locked: "true"`. Instead
// of synthesizing them virtually at render time (the old `__virt__` approach, which
// caused the s72 disappearance and the s73 duplicate-lock bugs), they are now seeded
// as REAL `generic_entries` rows with a DETERMINISTIC, sentinel-prefixed `rowId`.
//
// The prefix is the single source of truth for "is this a built-in locked row":
//   - the seeder (server/backfill-default-rows.ts) writes rows with these ids;
//   - the renderer (GenericCategoryPage) locks a row iff `isLockedBuiltIn(rowId)`;
//   - the "Gov Form" badge keys off the same id.
// User-added rows always get a random UUID, so they can never collide with — or be
// mistaken for — a built-in. `rowId` is a plain varchar (non-UUID ids are already
// tolerated end-to-end), so no schema change is needed.
//
// `index` is the position of the `_locked` row within the section's defaultRows
// AFTER filtering out version sentinels (`__v`). The seeder and every reader MUST
// use that same basis (see `strippedDefaultRows` callers) so ids line up.

export const LOCKED_BUILTIN_ROW_PREFIX = "__builtin__";

/** Deterministic rowId for a built-in locked row. Stable across clients (the
 *  (client_id, field_id, row_id) uniqueness is per-client) and immutable across
 *  edits, so the lock survives materialization and reload. */
export function lockedBuiltInRowId(sectionSlug: string, index: number): string {
  return `${LOCKED_BUILTIN_ROW_PREFIX}${sectionSlug}__${index}`;
}

/** True iff `rowId` is a seeded built-in locked row. The ONLY lock check — no
 *  value-matching, no inline string literals anywhere else. */
export function isLockedBuiltIn(rowId: string | null | undefined): boolean {
  return typeof rowId === "string" && rowId.startsWith(LOCKED_BUILTIN_ROW_PREFIX);
}

/**
 * s223 (D-222.10 part 5) — the sections whose built-in locked rows MAY be soft-hidden.
 *
 * `isRowHidden` exempts locked rows from hiding ("their template order must hold"; the comment recorded the
 * exemption as defensive, with none flagged). The interview's Government ID screen makes hiding one REAL and
 * RULED: a tile pick unhides the matching built-in row, un-picking hides it. This list is the narrow opening —
 * every other locked row (the Will row in Important Documents) keeps the exemption exactly as it was.
 */
export const HIDEABLE_LOCKED_SECTIONS: readonly string[] = ["personal-information-government-ids"];

/** True iff `rowId` is a locked built-in row of a section whose locked rows may be hidden. Pure. */
export function isHideableLockedBuiltIn(rowId: string | null | undefined): boolean {
  return isLockedBuiltIn(rowId) && HIDEABLE_LOCKED_SECTIONS.some((s) => rowId!.startsWith(`${LOCKED_BUILTIN_ROW_PREFIX}${s}__`));
}

// Seeded (non-locked) default rows (s76). Same deterministic-id idea as the
// locked scheme above, but a DISTINCT prefix so these rows are NOT treated as
// locked — they are editable placeholders. Giving them a stable, slot-derived id
// (instead of a random UUID) means editing the row's identifier field no longer
// orphans it from its wizard slot, so the value persists across reload and the
// backfill never re-mints a duplicate placeholder.
//   - the seeder (backfill-default-rows) writes non-locked default rows with these ids;
//   - the wizard (GuidedStagePage) re-associates a seeded step to its saved row by this id;
//   - user-ADDED rows still get a random UUID, so seeded vs user-added stays distinguishable.
// `index` is the row's position within the section's non-version (`__v`) defaultRows —
// the SAME basis the locked branch and the wizard use, so ids line up.
export const SEEDED_DEFAULT_ROW_PREFIX = "__seeded__";

/** Deterministic rowId for a non-locked seeded default row at slot `index`. */
export function seededDefaultRowId(sectionSlug: string, index: number): string {
  return `${SEEDED_DEFAULT_ROW_PREFIX}${sectionSlug}__${index}`;
}

/** True iff `rowId` is a seeded (non-locked) default row — i.e. seeded scaffolding,
 *  not a user-added row. */
export function isSeededDefaultRow(rowId: string | null | undefined): boolean {
  return typeof rowId === "string" && rowId.startsWith(SEEDED_DEFAULT_ROW_PREFIX);
}

// Curated FIXTURE rows (s186, RT#1 Shape 3). The test-account seeder curates a handful of
// records on `complete@test.com` so the 100%-complete showcase is actually complete. Until s186
// it addressed them by BARE UUID — ids captured from the staging database and hardcoded in a
// seeder that runs on every environment, prod included. Those ids do not exist on prod, so the
// curation path's create-if-absent branch minted five new rows there instead of curating existing
// ones (s185, four of them incomplete because the curation set was authored as a PATCH).
//
// 🔴 THE DEFECT WAS THE SHAPE, NOT THE TYPO: a per-client id captured from one environment can
// never be reproducible in another. A declared, slug-keyed id is identical everywhere by
// construction, which is the whole point of the family.
//
// Distinct prefix, deliberately:
//   - NOT `__seeded__` — a seeded rowId shades at 45% opacity as a template suggestion, and these
//     are real records. It also keeps them permanently out of scope for `__seeded__%` retirement
//     passes (server/migrations/mig-retire-suggestion-rows.ts matches on that prefix).
//   - NOT `__builtin__` — these are ordinary editable records, not locked structural rows.
//   - The `<key>` is a stable content slug (e.g. the institution), NOT an array index: unlike
//     `__seeded__…__N` these rows have no `defaultRows` slot to derive an index from.
//
// 🔴 This family is a DOWN PAYMENT on GUARD-CENSUS-1 #13, not its closure. A declared id proves
// the seeder addresses rows reproducibly; it does not prove the fixture as a whole can be
// regenerated from the seeder.
export const FIXTURE_ROW_PREFIX = "__fixture__";

/** Deterministic rowId for a curated fixture record. Identical on every environment. */
export function fixtureRowId(sectionSlug: string, key: string): string {
  return `${FIXTURE_ROW_PREFIX}${sectionSlug}__${key}`;
}

/** True iff `rowId` is a curated fixture record. */
export function isFixtureRow(rowId: string | null | undefined): boolean {
  return typeof rowId === "string" && rowId.startsWith(FIXTURE_ROW_PREFIX);
}

// Section-header row (s95, Option 2). An expandable section may declare ONE
// "header field" (section-layout-config `headerField`) — a single fact for the
// whole section (e.g. Organ Donor Status on Health & Medical), rendered ABOVE the
// per-record list. Its value lives on this single deterministic row, created
// lazily on first save (no backfill seeding needed) and excluded from the record
// list. One header row per section, so the id needs no index.
export const HEADER_ROW_PREFIX = "__header__";

/** Deterministic rowId for a section's header field value. */
export function headerRowId(sectionSlug: string): string {
  return `${HEADER_ROW_PREFIX}${sectionSlug}`;
}

/** True iff `rowId` is a section-header row (excluded from the record list). */
export function isHeaderRow(rowId: string | null | undefined): boolean {
  return typeof rowId === "string" && rowId.startsWith(HEADER_ROW_PREFIX);
}
